import { Component, HostListener, OnInit } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgxPaginationModule } from 'ngx-pagination';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { IDropdownSettings, NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';

export interface PalletTypes {
  palletTypesCode: string;
  palletTypeName: string;
  palletTypesGroupName: string;
  palletTypesGroupCode: string;
}

interface MergedPallet {
  oprPallet: ViewOprPallets;
  comPallet: ViewComPallets;
}

export interface ViewComPallets {
  palletComplementaryValueNumber: string;
  palletComplementaryValueDate: string;
  palletComplementaryStatus: string;
  palletComplementaryWarehouseCode: string;
  warehouseCodeName: string;
  palletComplementaryCarrierCode: string;
  carrierName: string;
  palletComplementaryValueStatus: string;
}

export interface ViewOprPallets {
  palletId: number;
  palletCode: string;
  palletWaybillCode: string;
  palletShipmentCode: string;
  palletDate: string;
  palletDeliveryType: string;
  palletDealerCode: string;
  dealerName: string;
  dealerOperatingAddressStreet: string;
  dealerOperatingAddressCity: string;
  palletStatus: string;
  palletActualDeliveryDate: string;
  palletEstimatedDeliveryDate: string;
  regionCode: string;
  regionName: string;
  palletCurrentStatus: string;
  palletOnTime: string;
  palletTypesGroupName: string;
  showComPallets: boolean;
  comPallets: ViewComPallets[];
}


interface Warehouse {
  warehouseCode: string;
  warehouseName?: string;
}


interface Client {
  clientCode: string;
  clientName?: string;
}

interface Brand {
  brandCode: string;
  brandName?: string;
}

interface ClientsBrands {
  clientCode: string;
  clientName?: string;
  brands: Brand[];
}

@Component({
  selector: 'app-returns-total',
  standalone: true,
  templateUrl: './returns-total.component.html',
  styleUrls: ['./returns-total.component.css'],
  imports: [FormsModule, CommonModule, TranslateModule, NgxPaginationModule, NgMultiSelectDropDownModule]
})
export class ReturnsTotalComponent implements OnInit {

  currentPage: number = 1;
  itemsPerPage: number = 25;

  mergedPallets: MergedPallet[] = [];
  filteredMergedPallets: MergedPallet[] = [];

  temp: MergedPallet[] = [];

  searchDealerCode: string = '';
  searchPalletCode: string = '';
  searchWaybillCode: string = '';
  dateStart: string = '';
  dateEnd: string = '';
  dateCreationStart: string = '';
  dateCreationEnd: string = '';
  
    palletTypesGroupCode: string = '';
    palletTypesCode: string = '';
    filteredPalletTypes: PalletTypes[] = [];
    uniquePalletTypesGroup: PalletTypes[] = [];
    filteredNamesByGroup: PalletTypes[] = [];
  

  errorStatus: string = '';

  dateError: string = '';
  isLoading: boolean = false;

  globalSearchTerm: string = '';
  palletStatus: string = '';     // "DELIVERED" | "PICKED_UP"

  showFilters: boolean = true;

  palletCurrentStatus: string = '';

  selectedOprPallet: ViewOprPallets | null = null;
  showModal: boolean = false;

  showNotification = false;
  notificationMessage = '';

  filteredWarehouses: Warehouse[] = [];
  warehouseCode: string = '';

  selectedDealerCode: string = '';
  filteredDealers: { dealerCode: string, dealerName: string, dealerOperatingAddressCity: string, count: number }[] = [];

  selectedCarrierCode: string = '';
  filteredCarriers: { carrierCode: string, carrierName: string, count: number }[] = [];

  selectedRegionCode: string = '';
  filteredRegions: { regionCode: string, regionName: string, count: number }[] = [];

  openedPalletId: number | null = null;

  sortState = { active: '', direction: '' };
  onTimeStatus: string = '';

  filteredBrands: Brand[] = [];
  selectedBrands: Brand[] = [];
  filteredClients: Client[] = [];
  selectedClients: Client[] = [];
  clientsBrands: ClientsBrands[] = [];
    
  dropdownSettingsBrand: IDropdownSettings = {};
  dropdownSettingsClient: IDropdownSettings = {};
  
  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    
    const stored = localStorage.getItem('userFunctions');
    if(stored) {
      const parsed = JSON.parse(stored);
      this.clientsBrands = parsed.clients; 

      this.filteredClients = this.clientsBrands.map(cb => ({
        clientCode: cb.clientCode,
        clientName: cb.clientName,
      }))
      .sort((a, b) => {
        if (!a.clientName && !b.clientName) return 0;       
        if (!a.clientName) return 1;                        
        if (!b.clientName) return -1;                       
        return a.clientName.localeCompare(b.clientName);   
      });

      const allBrands: Brand[] = this.clientsBrands.flatMap(cb =>
        cb.brands.map(b => ({
          brandCode: b.brandCode,
          brandName: b.brandName,
        }))
      ).sort((a, b) => {
        if (!a.brandName && !b.brandName) return 0;       
        if (!a.brandName) return 1;                        
        if (!b.brandName) return -1;                       
        return a.brandName.localeCompare(b.brandName);   
      });

      this.filteredBrands = Array.from(
        new Map(allBrands.map(b => [b.brandCode, b])).values()
      );

      this.dropdownSettingsBrand = {
        singleSelection: false,
        idField: 'brandCode',
        textField: 'brandName',
        selectAllText: 'Seleziona tutti',
        unSelectAllText: 'Deseleziona tutti',
        itemsShowLimit: 3,
        allowSearchFilter: true,
        searchPlaceholderText: 'Cerca'
      };
      this.dropdownSettingsClient = {
        singleSelection: false,
        idField: 'clientCode',
        textField: 'clientName',
        selectAllText: 'Seleziona tutti',
        unSelectAllText: 'Deseleziona tutti',
        itemsShowLimit: 3,
        allowSearchFilter: true,
        searchPlaceholderText: 'Cerca'
      };
    }

    const savedPage = localStorage.getItem('currentParcelPage');
    this.currentPage = savedPage ? +savedPage : 1;

    // Recupera anche itemsPerPage dal localStorage
    const savedItemsPerPage = localStorage.getItem('itemsPerPage');
    this.itemsPerPage = savedItemsPerPage ? +savedItemsPerPage : 25;

    this.mergedPallets = [];
    this.filteredMergedPallets = [];

    this.calcolaDataDefaultFiltri();

    this.loadWarehouses();
    this.loadPalletTypes();
    this.loadData();
  }

  
  onClientsChange(): void {
    this.getIntersectionClientsBrands();
  }

  getIntersectionClientsBrands() : void {
    if (!this.selectedClients || this.selectedClients.length === 0) {
      // Nessun client selezionato → mostro tutti i brands
      this.filteredBrands = Array.from(
        new Map(
          this.clientsBrands
            .flatMap(cb => cb.brands)
            .map(b => [b.brandCode, b])
        ).values()
      );
      this.selectedBrands = []; // reset
      return;
    }

    // prendo i codici dei client selezionati
    const selectedCodes = this.selectedClients.map(c => c.clientCode);

    // ottengo i brands per ogni client selezionato
    const brandLists = selectedCodes.map(code => {
      const client = this.clientsBrands.find(cb => cb.clientCode === code);
      return client ? client.brands : [];
    });


    // calcolo l’intersezione tra tutte le liste
    const intersection = brandLists.reduce((acc, curr) => {
      return acc.filter(b =>
        curr.some(x => x.brandCode === b.brandCode)
      );
    });


    // aggiorno i brands disponibili
    this.filteredBrands = intersection;
    //this.selectedBrands = []; // reset per evitare selezioni incoerenti
  }


  selectAllClients() {
    this.selectedClients = [...this.filteredClients];

    this.getIntersectionClientsBrands();
  }

  deselectAllClients() {
    this.selectedClients = [];

    this.getIntersectionClientsBrands();
  }

  selectAllBrands() {
    this.selectedBrands = [...this.filteredBrands];
  }

  deselectAllBrands() {
    this.selectedBrands = [];
  }


    getFilteredPallets(): MergedPallet[] {
      return this.mergedPallets
        .filter(m => 
          m.comPallet.palletComplementaryValueNumber != null &&
          m.comPallet.palletComplementaryValueNumber !== ''
        )
        .sort((a, b) => {
          const dateA = new Date(a.comPallet.palletComplementaryValueDate).getTime();
          const dateB = new Date(b.comPallet.palletComplementaryValueDate).getTime();
          return dateA - dateB; // crescente
          // return dateB - dateA; // decrescente
        });
    }

    loadWarehouses(): void {
      this.http.get<Warehouse[]>(`${environment.apiUrl}/tracking/warehouses`).subscribe({
        next: (data) => this.filteredWarehouses = data,
        error: () => console.error('Errore nel caricamento dei warehouse')
      });
    }
  
  
  loadPalletTypes(): void {
    this.http.get<PalletTypes[]>(`${environment.apiUrl}/pallets/types`).subscribe({
      next: (data) => {
        console.log("palletTypes", data); 
        this.uniquePalletTypesGroup = data.filter(
          (p, index, self) =>
            index === self.findIndex(other => other.palletTypesGroupCode === p.palletTypesGroupCode)
        );
        this.filteredPalletTypes = data;
      },
      error: () => console.error('Errore nel caricamento delle tipologie di reso')
    });
  }

  onItemsPerPageChange(): void {
    this.currentPage = 1; // Reset alla prima pagina quando cambi il numero di elementi
    localStorage.setItem('itemsPerPage', this.itemsPerPage.toString());
  }

  resetItemsPerPage(): void {
    this.itemsPerPage = 25;
    this.currentPage = 1;
    localStorage.setItem('itemsPerPage', '25');
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    localStorage.setItem('currentParcelPage', page.toString());
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.dateError = '';

    this.dateError = ''; // Pulisce eventuali errori precedenti

    const affidoStartSet = !!this.dateStart;
    const affidoEndSet = !!this.dateEnd;
    const creationStartSet = !!this.dateCreationStart;
    const creationEndSet = !!this.dateCreationEnd;

    const affidoDatesValid = affidoStartSet && affidoEndSet;
    const creationDatesValid = creationStartSet && creationEndSet;

    // Caso 1: tutti vuoti
    const noDatesSet = !affidoStartSet && !affidoEndSet && !creationStartSet && !creationEndSet;

    // Caso 2: uno dei due gruppi è compilato parzialmente
    const affidoPartial = affidoStartSet !== affidoEndSet;
    const creationPartial = creationStartSet !== creationEndSet;

    if (noDatesSet || affidoPartial || creationPartial) {
      this.dateError = "Inserisci entrambe le date per l'affido o per la creazione.";
      return;
    }

    // Controllo coerenza date creazione
    if (affidoDatesValid) {
      const start = new Date(this.dateStart);
      const end = new Date(this.dateEnd);
      if (start > end) {
        this.dateError = "La data di inizio affido non può essere successiva alla data di fine.";
        return;
      }
    }

    // Controllo coerenza date consegna
    if (creationDatesValid) {
      const dStart = new Date(this.dateCreationStart);
      const dEnd = new Date(this.dateCreationEnd);
      if (dStart > dEnd) {
        this.dateError = 'La data di inizio creazione non può essere successiva alla data di fine.';
        return;
      }
    }

    this.loadWarehouses();
    this.loadData();
  }

  loadData(): void {
    this.isLoading = true;

    let clientsBrandsSelected: ClientsBrands[];
    if(this.filteredClients.length == 1) {
      this.selectedClients = [...this.filteredClients];
    }
    if (this.selectedClients.length > 0) {
      clientsBrandsSelected = this.selectedClients.map(sc => {
        // trovo il client originale da clientsBrands
        const originalClient = this.clientsBrands.find(cb => cb.clientCode === sc.clientCode);

        var filteredBrandsFromClient: Brand[] = [];
        if(this.selectedBrands.length > 0) {
          // brands effettivi = intersezione tra quelli del client e quelli selezionati
          filteredBrandsFromClient = (originalClient?.brands || []).filter(b =>
            this.selectedBrands.some(sb => sb.brandCode === b.brandCode)
          );
        }
        else {
          filteredBrandsFromClient = this.filteredBrands;
        }

        return {
          clientCode: sc.clientCode,
          clientName: sc.clientName,
          brands: filteredBrandsFromClient
        } as ClientsBrands;
      });
    } else if (this.selectedBrands && this.selectedBrands.length > 0) {
      // nessun client selezionato, ma brand sì
      clientsBrandsSelected = this.clientsBrands
        .map(cb => {
          const matchingBrands = cb.brands.filter(b =>
            this.selectedBrands.some(sb => sb.brandCode === b.brandCode)
          );
          if (matchingBrands.length > 0) {
            return {
              clientCode: cb.clientCode,
              clientName: cb.clientName,
              brands: matchingBrands
            } as ClientsBrands;
          }
          return null;
        })
        .filter(cb => cb !== null) as ClientsBrands[];

    } else {
      // né client né brand selezionati → ritorno tutto
      clientsBrandsSelected = this.clientsBrands;
    }

    const body = {
      waybillCode: this.searchWaybillCode || '',
      palletCode: this.searchPalletCode || '',
      dateStart: this.dateStart,
      dateEnd: this.dateEnd,
      dealerCode: this.searchDealerCode !== '' ? this.searchDealerCode : this.selectedDealerCode,
      warehouseCode: this.warehouseCode || '',
      regionCode: this.selectedRegionCode,
      carrierCode: this.selectedCarrierCode,
      palletStatus: this.palletStatus,
      dateCreationStart: this.dateCreationStart,
      dateCreationEnd: this.dateCreationEnd,
      palletTypesGroupCode: this.palletTypesGroupCode,
      palletTypesCode: this.palletTypesCode,
      clientsBrands: clientsBrandsSelected
    };

    this.http.post<ViewOprPallets[]>(
      `${environment.apiUrl}/pallets/total`,
      body
    ).subscribe({
      next: (data: ViewOprPallets[]) => {
        console.log("data pallets: ", data);

        // Genera MergedPallet[] da ViewOprPallets[] + comPallets
        this.mergedPallets = data.flatMap(opr =>
          (opr.comPallets ?? []).map(com => ({
            oprPallet: opr,
            comPallet: com
          }))
        );

        this.filteredMergedPallets = [...this.mergedPallets];
        this.applyFrontendFilters();
        this.updateDealerFilterOptions(data);
        this.updateCarrierFilterOptions(data);
        this.updateRegionFilterOptions(data);

        this.isLoading = false;
      },
      error: (err) => {
        console.error('Errore durante la chiamata HTTP:', err);
        this.isLoading = false;
      }
    });
  }



  // toggleComPallets(pallet: ViewOprPallets) {
  //   if(pallet.comPallets.filter(c => c.palletComplementaryValueNumber != null && c.palletComplementaryValueNumber !== '').length > 0) {
  //     pallet.showComPallets = !pallet.showComPallets;
  //   }
  //   else {
  //     pallet.showComPallets = false;
  //   }
  // }

  toggleComPallets(p: any): void {
    if (this.openedPalletId === p.palletId) {
      this.openedPalletId = null; // chiude se clicco sulla stessa riga aperta
    } else {
      this.openedPalletId = p.palletId; // apre quella cliccata e chiude eventuale precedente
    }
  }

  onGlobalSearchChange() {
    const term = this.globalSearchTerm.toLowerCase();

    if (!term) {
      this.filteredMergedPallets = [...this.temp];
      return;
    }

    this.filteredMergedPallets = this.filteredMergedPallets.filter(pallet => {
      return Object.values(pallet).some(val =>
        val != null && val.toString().toLowerCase().includes(term)
      );
    });
  }

  toDateOnly(d: string | Date): string {
    return new Date(d).toISOString().split('T')[0];
  }

  updateDealerFilterOptions(pallets: ViewOprPallets[]) {
    const dealerMap = new Map<string, { dealerName: string, dealerOperatingAddressCity: string, count: number }>();

    for (const pallet of pallets) {
      const code = pallet.palletDealerCode;
      const dealerOperatingAddressCity = pallet.dealerOperatingAddressCity;
      const name = pallet.dealerName || 'Dealer sconosciuto';

      if (!dealerMap.has(code)) {
        dealerMap.set(code, { dealerName: name, dealerOperatingAddressCity: dealerOperatingAddressCity, count: 1 });
      } else {
        dealerMap.get(code)!.count++;
      }
    }

    this.filteredDealers = Array.from(dealerMap.entries())
    .sort(([, a], [, b]) => a.dealerName.localeCompare(b.dealerName))
    .map(([dealerCode, data]) => ({
      dealerCode,
      dealerName: data.dealerName,
      dealerOperatingAddressCity: data.dealerOperatingAddressCity,
      count: data.count
    }));
  }

  updateCarrierFilterOptions(pallets: ViewOprPallets[]) {
    const carrierMap = new Map<string, { carrierName: string, count: number }>();

    for (const pallet of pallets) {
      for(const com of pallet.comPallets) {
        if(com.palletComplementaryCarrierCode != null) {
          const code = com.palletComplementaryCarrierCode;
          const name = com.carrierName || 'Carrier sconosciuto';

          if (!carrierMap.has(code)) {
            carrierMap.set(code, { carrierName: name, count: 1 });
          } else {
            carrierMap.get(code)!.count++;
          }
        }
      }
    }

    this.filteredCarriers = Array.from(carrierMap.entries())
    .sort(([, a], [, b]) => a.carrierName.localeCompare(b.carrierName))
    .map(([carrierCode, data]) => ({
      carrierCode,
      carrierName: data.carrierName,
      count: data.count
    }));
  }

  updateRegionFilterOptions(pallets: ViewOprPallets[]) {
    const regionMap = new Map<string, { regionName: string, count: number }>();

    for (const pallet of pallets) {
      if(pallet.regionName == null)
        console.log("dealerCode " +  pallet.palletDealerCode  + "regione name " +  pallet.regionName + "regione code " +  pallet.regionCode );
      const code = pallet.regionCode;
      const name = pallet.regionName;

      if (!regionMap.has(code)) {
        regionMap.set(code, { regionName: name, count: 1 });
      } else {
        regionMap.get(code)!.count++;
      }
    }

    this.filteredRegions = Array.from(regionMap.entries())
    .filter(([, n]) =>  n.regionName != null)
    .sort(([, a], [, b]) => a.regionName.localeCompare(b.regionName))
    .map(([regionCode, data]) => ({
      regionCode,
      regionName: data.regionName,
      count: data.count
    }));
  }

  exportParcelsDetailsToExcel(): void {
    const exportData = this.filteredMergedPallets.filter(c => c.comPallet.palletComplementaryValueNumber != null && c.comPallet.palletComplementaryValueNumber !== '')
        .sort((a, b) => Number(a.comPallet.palletComplementaryValueDate) - Number(b.comPallet.palletComplementaryValueDate)).map(merge => ({
        'Codice Reso': merge.oprPallet.palletCode,
        'Tipologia': merge.oprPallet.palletTypesGroupName,
        'Codice Bolla': merge.oprPallet.palletWaybillCode,
        'Contenuto': merge.oprPallet.palletDeliveryType,
        'Data Affido': this.formatDate(merge.oprPallet.palletDate),
        'Stato': merge.comPallet.palletComplementaryValueNumber,
        'Data Stato': this.formatDate(merge.comPallet.palletComplementaryValueDate),
        'Errore Stato': merge.comPallet.palletComplementaryValueStatus,
        'Magazzino': merge.comPallet.warehouseCodeName,
        'Vettore': merge.comPallet.carrierName,
        'Tipo Consegna': merge.oprPallet.palletDeliveryType,
        'Codice Dealer': merge.oprPallet.palletDealerCode,
        'Dealer': merge.oprPallet.dealerName,
        'Indirizzo Dealer': merge.oprPallet.dealerOperatingAddressStreet,
        'Città Dealer': merge.oprPallet.dealerOperatingAddressCity,
        'Data Consegna Effettiva': merge.oprPallet.palletStatus === 'DELIVERED' ? this.formatDate(merge.oprPallet.palletActualDeliveryDate) : '',
        'Data Consegna Stimata': this.formatDate(merge.oprPallet.palletEstimatedDeliveryDate),
        'Regione': merge.oprPallet.regionName,
        'Codice Spedizione': merge.oprPallet.palletShipmentCode
      }));

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
    const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    const blobData = new Blob([excelBuffer], { type: 'application/octet-stream' });
    const now = new Date();
    const timestamp = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;

    FileSaver.saveAs(blobData, `pallets-details_${timestamp}.xlsx`);
  }

  // private translatePalletStatus(status: string): string {
  //   switch (status) {
  //     case 'REQUEST_RETURN': return 'Richiesta di reso';
  //     case 'PICKED_UP': return 'Preso in carico';
  //     case 'SUSPENDED': return 'Sospeso';
  //     case 'DELIVERED': return 'Consegnato';
  //     default: return status;
  //   }
  // }

  // Formatta date in formato dd/MM/yyyy HH:mm
  private formatDate(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }


  resetFilters(): void {
    this.searchDealerCode = '';
    this.selectedDealerCode = '';
    this.palletStatus = '';
    this.searchPalletCode = '';
    this.searchWaybillCode = '';
    this.selectedCarrierCode = '';
    this.warehouseCode = '';
    this.selectedRegionCode = '';
    this.dateCreationStart = '';
    this.dateCreationEnd = '';
    this.onTimeStatus = '';
    this.palletTypesCode = '';
    this.palletTypesGroupCode = '';
    this.errorStatus = '';
    this.selectedBrands = [];
    this.selectedClients = [];
    this.dateStart = '';
    this.dateEnd = '';

    this.calcolaDataDefaultFiltri();

    // Applica di nuovo il filtro (se necessario)
    this.applyFilters();
  }



  calcolaDataDefaultFiltri(): void {
    // Calcolo data predefinita (ieri o venerdì)
    const today = new Date();
    let defaultDate = new Date(today);

    if (today.getDay() === 0) {
      // Domenica → venerdì
      defaultDate.setDate(today.getDate() - 2);
    } else if (today.getDay() === 1) {
      // Lunedì → venerdì
      defaultDate.setDate(today.getDate() - 3);
    } else {
      // Altri giorni → ieri
      defaultDate.setDate(today.getDate() - 1);
    }

    const formattedDate = defaultDate.toISOString().split('T')[0];
    this.dateStart = formattedDate;
    this.dateEnd = formattedDate;
  }

  shiftAffidoDates(days: number): void {
    if (this.dateStart) {
      this.dateStart = this.shiftDate(this.dateStart, days);
    }
    else {
      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0];
      this.dateStart = this.shiftDate(formattedDate, days);
    }
    if (this.dateEnd) {
      this.dateEnd = this.shiftDate(this.dateEnd, days);
    }
    else {
      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0];
      this.dateEnd = this.shiftDate(formattedDate, days);
    }
  }

  shiftCreationDates(days: number): void {
    if (this.dateCreationStart) {
      this.dateCreationStart = this.shiftDate(this.dateCreationStart, days);
    }
    else {
      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0];
      this.dateCreationStart = this.shiftDate(formattedDate, days);
    }
    if (this.dateCreationEnd) {
      this.dateCreationEnd = this.shiftDate(this.dateCreationEnd, days);
    }
    else {
      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0];
      this.dateCreationEnd = this.shiftDate(formattedDate, days);
    }
  }

  private shiftDate(dateStr: string, days: number): string {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + days);
    return date.toISOString().substring(0, 10); // Format: yyyy-mm-dd
  }

  applyFrontendFilters(): void {
    this.filteredMergedPallets = this.mergedPallets.filter(merge => {
      const matchesCurrentStatus =
        !this.palletCurrentStatus ||
        (merge.comPallet.palletComplementaryValueNumber != null &&
        merge.comPallet.palletComplementaryValueNumber.toString() === this.palletCurrentStatus);

      const matchesOnTime =
        !this.onTimeStatus || merge.oprPallet.palletOnTime === '' || merge.oprPallet.palletOnTime === this.onTimeStatus;

      const matchesErrorStatus =
        this.errorStatus === "ALL" ||
        (this.errorStatus === "" &&
          (merge.comPallet.palletComplementaryValueStatus === null ||
          merge.comPallet.palletComplementaryValueStatus === "DATA_CHANGED")) ||
        merge.comPallet.palletComplementaryValueStatus === this.errorStatus;


      return matchesCurrentStatus && matchesOnTime && matchesErrorStatus;
    });

    this.temp = [...this.filteredMergedPallets]; // per ricerca globale
  }


  setSort(column: string): void {
    if (this.sortState.active === column) {
      // Toggle direction
      this.sortState.direction = this.sortState.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortState.active = column;
      this.sortState.direction = 'asc';
    }
    this.applySortManual();
    this.currentPage = 1;
  }


  applySortManual(): void {
    if (this.sortState.active && this.sortState.direction) {
      const isAsc = this.sortState.direction === 'asc';
      const sortField = this.sortState.active;

      this.filteredMergedPallets = [...this.filteredMergedPallets].sort((a, b) => {
        const aValue = this.resolveSortValue(a, sortField);
        const bValue = this.resolveSortValue(b, sortField);
        return this.compare(aValue, bValue, isAsc);
      });
    }
  }

  resolveSortValue(obj: MergedPallet, field: string): any {
    // Aggiungi qui i nomi reali dei campi e dove si trovano
    const oprFields = [
      'palletCode', 'palletWaybillCode', 'palletDate', 'palletDeliveryType',
      'palletDealerCode', 'dealerName', 'dealerOperatingAddressStreet', 
      'dealerOperatingAddressCity', 'palletStatus', 'palletActualDeliveryDate',
      'palletEstimatedDeliveryDate', 'regionName', 'palletCurrentStatus', 'palletShipmentCode'
    ];

    const comFields = [
      'palletComplementaryValueNumber', 'palletComplementaryValueDate',
      'warehouseCodeName', 'carrierName', 'palletComplementaryStatus'
    ];

    if (oprFields.includes(field)) {
      return obj.oprPallet[field as keyof ViewOprPallets];
    }

    if (comFields.includes(field)) {
      return obj.comPallet?.[field as keyof ViewComPallets] ?? '';
    }

    return '';
  }



  compare(a: string | number, b: string | number, isAsc: boolean): number {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

    onGroupChange() {
    this.palletTypesCode = ''; // reset del secondo filtro
    this.filteredNamesByGroup = this.filteredPalletTypes.filter(
      pt => pt.palletTypesGroupCode === this.palletTypesGroupCode
    );
  }
}

